import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { DashboardService } from "../../dashboard.service";
import { DashboardStore } from "../../dashboard.store";
import { GenericDrawerService } from "../../drawer/global-drawer.service";
import { WorkspacesService } from "../../main/accountant/workspaces.service";
import { RealTimeService } from "../../main/common/notifications/real-time.service";
import { QuickbooksIntegrationService } from "../../quickbooks-integration.service";
import { Company, SharedDataService } from "../../shared-data.service";
import { ConversationsService } from "../conversations.service";
import { HotToastService } from "@ngneat/hot-toast";
import { MomentModule } from "ngx-moment";
import { ClientTaskCommentsComponent } from "../client-task-comments/client-task-comments.component";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { QboClientTaskItemComponent } from "./qbo-client-task-item/qbo-client-task-item.component";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import {
  NgClass,
  NgIf,
  NgFor,
  KeyValuePipe,
  CommonModule,
} from "@angular/common";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { DeleteConfirmationModalComponent } from "../../main/accountant/modals/delete-confirmation-modal.component";
import { DrawerModule } from "primeng/drawer";

@Component({
  selector: "ch-qbo-client-tasks",
  templateUrl: "./qbo-client-tasks.component.html",
  styleUrls: ["./qbo-client-tasks.component.scss"],
  imports: [
    NgClass,
    NgIf,
    TooltipModule,
    NgFor,
    CommonModule,
    QboClientTaskItemComponent,
    BsDropdownModule,
    ClientTaskCommentsComponent,
    KeyValuePipe,
    MomentModule,
    DrawerModule,
  ],
})
export class QboClientTasksComponent implements OnInit {
  @Input() conversation: any = {};
  @Input() qboTrackedItems = {
    expense_items: [],
    deposit_items: [],
    receipt_requests: [],
  };
  @Input() accounts = [];
  @Input() vendors = [];
  @Input() classes = { enabled: false, classes: [] };
  @Input() salesTaxes = { enabled: false, sales_taxes: [] };
  @Input() hasSelectableClass = false;
  @Input() hasSelectableSalesTax = false;
  selectedTrackedItem: any = null;
  drawerVisible: boolean = false;
  qboTrackedItemsCounts = {
    unanswered_count: 0,
    to_review_count: 0,
  };

  @Input() company: Company = null;
  user = {};

  sub: any;

  loading = false;

  batchSelectedItems = [];

  moment_calendar_format = {
    sameDay: "[Today -] h:mm A",
    nextDay: "[Tomorrow]",
    nextWeek: "[Next] dddd",
    lastDay: "[Yesterday -] H:mm A",
    lastWeek: "MMM D - h:mm A",
    sameElse: "MMM D - h:mm A",
  };

  constructor(
    public genericDrawerService: GenericDrawerService,
    public dashboardStore: DashboardStore,
    private localStorageService: LocalStorageService,
    private realTimeService: RealTimeService,
    private workspacesService: WorkspacesService,
    private conversationsService: ConversationsService,
    private dashboardService: DashboardService,
    private quickbooksIntegrationService: QuickbooksIntegrationService,
    private sharedDataService: SharedDataService,
    private toastr: HotToastService,
    private modalService: BsModalService,
    private bsModalRef: BsModalRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.workspacesService.$conversation.subscribe((conversation) => {
      this.conversation = conversation;
      if (this.conversation != null) {
        if (this.conversation.id != null) {
          this.realTimeService.setConversationUpdatesListener(
            this.conversation.uuid,
          );

          this.realTimeService.conversationUpdates$.subscribe(
            (message: any) => {
              const trackedUuid =
                message?.tracked_item?.uuid ?? message?.tracked_item_uuid;
              const isDelete = message?.action === "delete";

              for (const [transactionType, accounts] of Object.entries(
                this.qboTrackedItems,
              )) {
                for (const [account, items] of Object.entries(accounts)) {
                  const index = items.findIndex(
                    (item) => item.uuid === trackedUuid,
                  );

                  if (index !== -1) {
                    if (isDelete) {
                      const deletedItemStatus =
                        this.qboTrackedItems[transactionType][account][index]
                          .answer_status;
                      this.qboTrackedItems[transactionType][account] =
                        items.filter((item) => item.uuid !== trackedUuid);
                      this.quickbooksIntegrationService.qboTrackedItemsCounts.update(
                        (prev) => ({
                          unanswered_count:
                            deletedItemStatus === "open"
                              ? Math.max(prev.unanswered_count - 1, 0)
                              : prev.unanswered_count,

                          to_review_count:
                            deletedItemStatus === "closed" ||
                            deletedItemStatus === "answered"
                              ? Math.max(prev.to_review_count - 1, 0)
                              : prev.to_review_count,
                        }),
                      );
                      if (
                        this.qboTrackedItems[transactionType][account]
                          .length === 0
                      ) {
                        delete this.qboTrackedItems[transactionType][account];
                      }
                    } else {
                      this.qboTrackedItems[transactionType][account][index] =
                        message.tracked_item;
                    }
                    break;
                  }
                }
              }

              this.cdr.detectChanges();
            },
          );

          this.loadQboTrackedItems();
        }
      }
    });

    this.workspacesService.$company.subscribe((company) => {
      this.company = company;

      if (this.company != null) {
        if (this.company.id != null) {
          this.getClasses();
          this.getSalesTaxes();
          this.getAccounts();
          this.getVendors();
        }
      }
    });

    this.sub =
      this.conversationsService.getTrackedItemsTabCountSubject.subscribe(
        (sub) => {
          this.loadQboTrackedItems();
        },
      );

    this.workspacesService.removeTrackedItem.subscribe((res) => {
      if (this.selectTrackedItem != null) {
        if (res.uuid == this.selectedTrackedItem.uuid) {
          this.selectedTrackedItem = null;
        }
      }

      this.loadQboTrackedItems();
    });
  }

  handleBatchSelectItems(items: any[]) {
    this.batchSelectedItems = items;
  }

  private getClasses() {
    this.quickbooksIntegrationService.retrieveClasses(this.company.id).then(
      (res) => {
        this.hasSelectableClass = res.enabled;
        this.classes = res.classes;
      },
      (err) => {
        if (err.status == 404) {
          this.hasSelectableClass = false;
        }
      },
    );
  }

  private getSalesTaxes() {
    this.quickbooksIntegrationService.retrieveSalesTaxes(this.company.id).then(
      (res) => {
        this.hasSelectableSalesTax = res.enabled;
        this.salesTaxes = res.tax_codes;
        console.log("salesTaxes", this.salesTaxes);
        console.log("hasSelectableSalesTax", this.hasSelectableSalesTax);
      },
      (err) => {
        console.log(err);
        this.hasSelectableSalesTax = false;
      },
    );
  }

  private getAccounts() {
    this.quickbooksIntegrationService
      .getAccountsByCompanyId(this.company.id)
      .subscribe(
        (res) => {
          this.accounts = res;
        },
        (err) => {
          console.log(err);
          this.accounts = [];
        },
      );
  }

  private getVendors() {
    this.quickbooksIntegrationService
      .getVendorsByCompanyId(this.company.id)
      .subscribe(
        (res) => {
          this.vendors = res;
        },
        (err) => {
          console.log(err);
          this.vendors = [];
        },
      );
  }

  ngOnDestroy() {
    if (this.sub != null) {
      this.sub.unsubscribe();
    }
  }

  loadQboTrackedItems() {
    this.loading = true;
    this.workspacesService
      .get_qbo_tracked_items(this.conversation.id, "open")
      .then(
        (res: any) => {
          this.qboTrackedItems = {
            expense_items: res.expense_items,
            deposit_items: res.deposit_items,
            receipt_requests: res.receipt_requests,
          };

          this.qboTrackedItemsCounts = res.counts;
          this.loading = false;
          console.log({ qboTrackedItems: this.qboTrackedItems });
          this.quickbooksIntegrationService.qboTrackedItemsCounts.set(
            res.counts,
          );
        },
        (err) => {
          console.log(err);
          this.loading = false;
        },
      );
  }

  ngAfterViewInit() {}

  selectTrackedItem(tracked_item) {
    this.selectedTrackedItem = tracked_item;
    this.drawerVisible = true;

    if (this.selectedTrackedItem != null) {
      Object.keys(this.qboTrackedItems).forEach((transactionType) => {
        Object.keys(this.qboTrackedItems[transactionType]).forEach(
          (account) => {
            for (
              let i = 0;
              i < this.qboTrackedItems[transactionType][account].length;
              i++
            ) {
              this.qboTrackedItems[transactionType][account][i].selected =
                false;
            }
          },
        );
      });

      this.selectedTrackedItem.selected = true;
    }
  }

  public openDeleteTrackedItemModal() {
    this.dashboardService.openDeleteTrackedItemModal.next(
      this.selectedTrackedItem,
    );
  }

  openBatchDeleteTrackedItemsModal() {
    const initialState = {
      displayName: `${this.batchSelectedItems.length} client tasks`,
      class: "modal-md",
    };
    this.bsModalRef = this.modalService.show(DeleteConfirmationModalComponent, {
      initialState,
    });
    this.bsModalRef.content.onClose.subscribe(async (res: boolean) => {
      if (res) {
        this.conversationsService
          .deleteTrackedItem(this.batchSelectedItems)
          .subscribe((res: any) => {
            if (res.status === 200) {
              this.toastr.success(res.message);
              this.loadQboTrackedItems();
              this.selectedTrackedItem = null;
              this.batchSelectedItems = [];
              return;
            }
            this.toastr.error(res.message);
          });
      }
    });
  }

  hasQboItems(key: string) {
    return Object.keys(this.qboTrackedItems[key]).length > 0;
  }

  goToQuickBooks(tracked_item): void {
    const id =
      tracked_item?.integration_data?.purchase_id != null
        ? tracked_item?.integration_data?.purchase_id
        : tracked_item?.integration_data?.deposit_id;

    window.open(
      `https://app.qbo.intuit.com/app/${
        tracked_item?.integration_data?.purchase_id != null
          ? "expense"
          : "deposit"
      }?txnId=${id}
    `,
      "_blank",
    );
  }

  refresh() {
    this.loading = true;
    this.quickbooksIntegrationService.refreshCache(this.company.id).subscribe(
      () => {
        // trigger refresh
        this.getClasses();
        this.getSalesTaxes();
        this.getAccounts();
        this.getVendors();

        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.toastr.error(
          "Error refreshing your connection. Please remove this connection and try again.",
        );
      },
    );
  }

  closeCallback(event: any) {
    this.drawerVisible = false;
    this.selectedTrackedItem = null;
  }
}
